import { useMemo, useState } from 'react';
import {
  MAX_PRICE,
  MIN_PRICE,
  PRICE_MESSAGE_KEY,
  isPriceOutOfBounds,
  priceMessageParams,
} from '~/utils/moneyBounds';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';
import {
  useGetShippingRates,
  useUpsertFacilityShippingRates,
} from '~/api/facility';
import type { GetShippingRatesQuery, Currency } from '~/graphql/graphql';
import { Button } from '~/components/ui';
import { formatNumberWithoutDecimals } from '~/utils';
import type { Facility } from './OrganizationFacilities';

type FacilityShippingRateData = Facility['shippingRates'][number];

type ShippingRateReference = GetShippingRatesQuery['shippingRates'][number];

type ZoneRateRowProps = {
  shippingRate: ShippingRateReference;
  value: string;
  onChange: (shippingRateId: string, value: string) => void;
};

function ZoneRateRow({ shippingRate, value, onChange }: ZoneRateRowProps) {
  const { t } = useTranslation('settings');

  const zoneLabel = t(
    `settings.facilities.shipping_rates.zones.${shippingRate.zone}`,
    {
      defaultValue: shippingRate.zone,
    }
  );

  const range =
    shippingRate.distanceInMileMax == null
      ? t('settings.facilities.shipping_rates.distance_range_open', {
          min: formatNumberWithoutDecimals(shippingRate.distanceInMileMin),
        })
      : t('settings.facilities.shipping_rates.distance_range', {
          min: formatNumberWithoutDecimals(shippingRate.distanceInMileMin),
          max: formatNumberWithoutDecimals(shippingRate.distanceInMileMax),
        });

  const transit =
    shippingRate.shippingDaysMin === shippingRate.shippingDaysMax
      ? t('settings.facilities.shipping_rates.transit_days', {
          count: shippingRate.shippingDaysMin,
        })
      : t('settings.facilities.shipping_rates.transit_range', {
          min: shippingRate.shippingDaysMin,
          max: shippingRate.shippingDaysMax,
        });

  return (
    <div className="flex items-center gap-2 bg-neut-50 rounded-lg px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium text-neut-700">
          {zoneLabel} · {range}
        </p>
        <p className="text-[10px] text-neut-400">{transit}</p>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-neut-500">$</span>
        <input
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          step="0.01"
          placeholder="—"
          value={value}
          onChange={(event) => onChange(shippingRate.id, event.target.value)}
          aria-label={t('settings.facilities.shipping_rates.rate_aria_label', {
            zone: zoneLabel,
          })}
          className="w-16 px-1.5 py-1 border border-neut-200 rounded text-xs text-right focus:outline-none focus:border-prim bg-neut-bg-alt"
        />
        <span className="text-[10px] text-neut-400">
          {t('settings.facilities.shipping_rates.per_unit')}
        </span>
      </div>
    </div>
  );
}

function rateToString(ratePerMbf: number | null | undefined): string {
  if (ratePerMbf == null) return '';
  return String(ratePerMbf);
}

// Normalizes a rate string so that '125.50' and '125.5' compare as equal.
function normalizeRate(value: string): string {
  if (value === '') return '';
  const parsed = parseFloat(value);
  return isNaN(parsed) ? '' : String(parsed);
}

type Props = {
  facilityId: string;
  initialRates: FacilityShippingRateData[];
};

export function ShippingRatesSection({ facilityId, initialRates }: Props) {
  const { t } = useTranslation('settings');
  const [isExpanded, setIsExpanded] = useState(false);
  // Keeps the Save button disabled from click until the post-save query refetch
  // completes and rates are synced, preventing accidental double-saves.
  const [isSyncingAfterSave, setIsSyncingAfterSave] = useState(false);

  const { data: shippingRatesData } = useGetShippingRates();

  const sortedReferenceRates = useMemo<ShippingRateReference[]>(() => {
    return [...(shippingRatesData?.shippingRates ?? [])].sort((rateA, rateB) =>
      rateA.zone.localeCompare(rateB.zone)
    );
  }, [shippingRatesData]);

  const isLoadingRates = sortedReferenceRates.length === 0;

  const serverRates = useMemo(() => {
    const existingByShippingRateId = Object.fromEntries(
      initialRates.map((facilityRate) => [
        facilityRate.shippingRate.id,
        facilityRate.ratePerMbf,
      ])
    );
    return Object.fromEntries(
      sortedReferenceRates.map((shippingRate) => [
        shippingRate.id,
        rateToString(existingByShippingRateId[shippingRate.id]),
      ])
    );
  }, [initialRates, sortedReferenceRates]);

  const [rates, setRates] = useState<Record<string, string>>(serverRates);
  const [prevServerRates, setPrevServerRates] = useState(serverRates);

  if (prevServerRates !== serverRates) {
    setPrevServerRates(serverRates);
    setRates(serverRates);
    setIsSyncingAfterSave(false);
  }

  const isDirty = sortedReferenceRates.some(
    (shippingRate) =>
      normalizeRate(rates[shippingRate.id] ?? '') !==
      normalizeRate(serverRates[shippingRate.id] ?? '')
  );

  const { mutate: upsertRates, isPending } = useUpsertFacilityShippingRates();

  function handleRateChange(shippingRateId: string, value: string) {
    setRates((prev) => ({ ...prev, [shippingRateId]: value }));
  }

  function handleSave() {
    function toRatePerMbf(value: string): number | null {
      const parsed = parseFloat(value);
      return value === '' || isNaN(parsed) ? null : parsed;
    }

    const shippingRates = sortedReferenceRates.map((shippingRate) => ({
      shippingRateId: shippingRate.id,
      ratePerMbf: toRatePerMbf(rates[shippingRate.id]),
      rateCurrency: 'USD' as Currency,
    }));

    if (shippingRates.some((rate) => isPriceOutOfBounds(rate.ratePerMbf))) {
      toast.error(t(PRICE_MESSAGE_KEY, priceMessageParams));
      return;
    }

    upsertRates(
      { facilityId, shippingRates },
      {
        onSuccess: () => {
          toast.success(t('settings.facilities.shipping_rates.save_success'));
          setIsSyncingAfterSave(true);
        },
        onError: () =>
          toast.error(t('settings.facilities.shipping_rates.save_error')),
      }
    );
  }

  return (
    <div
      data-testid="shipping-rates-section"
      className="mt-4 border-t border-neut-100 pt-4"
    >
      <div className="flex items-center gap-2 mb-1">
        <Calculator size={14} className="text-neut-prim" />
        <span className="text-xs font-semibold text-neut-700">
          {t('settings.facilities.shipping_rates.title')}
        </span>
      </div>
      <p className="text-[10px] text-neut-400 mb-3">
        {t('settings.facilities.shipping_rates.description')}
      </p>
      {isExpanded ? (
        isLoadingRates ? (
          <p className="text-xs text-neut-400">
            {t('settings.facilities.shipping_rates.loading')}
          </p>
        ) : (
          <>
            <div className="grid  sm:grid-cols-2 gap-2">
              {sortedReferenceRates.map((shippingRate) => (
                <ZoneRateRow
                  key={shippingRate.id}
                  shippingRate={shippingRate}
                  value={rates[shippingRate.id] ?? ''}
                  onChange={handleRateChange}
                />
              ))}
            </div>
            <Button
              type="button"
              onClick={handleSave}
              size="sm"
              disabled={!isDirty || isPending || isSyncingAfterSave}
              width="auto"
            >
              {isPending
                ? t('settings.facilities.shipping_rates.saving')
                : t('settings.facilities.shipping_rates.save_button')}
            </Button>
          </>
        )
      ) : (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="text-xs text-prim hover:text-prim-hover underline"
        >
          {t('settings.facilities.shipping_rates.load_button')}
        </button>
      )}
    </div>
  );
}
